import { useState, useEffect } from 'react'
import { UploadDocuments } from '../UploadDocuments'
import { SelectFolder } from '../SelectFolder'
import { IoDocumentTextOutline, IoImageOutline } from 'react-icons/io5'

const allowedTypes = [
	'application/pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'text/plain',
	'image/png',
	'image/jpeg',
	'image/jpg',
	'image/gif',
	'image/webp',
]

export const ModalDocument = ({ isOpen, onClose, folders = [], onSubmit, tagList, initialFolderId }) => {
	const user = JSON.parse(localStorage.getItem('user'))
	const isEmployee = window.location.pathname.includes('/employee/')
	const companyId = isEmployee ? user.companyId : user.id
	const [selectedFiles, setSelectedFiles] = useState([])
	const [selectedFolder, setSelectedFolder] = useState(null)
	const [fileTags, setFileTags] = useState({})
	const [step, setStep] = useState(1)
	const [fileError, setFileError] = useState('')
	const [tagSearch, setTagSearch] = useState('')

	useEffect(() => {
		if (isOpen && initialFolderId) {
			setSelectedFolder(initialFolderId)
		}
	}, [isOpen, initialFolderId])

	const handleClose = () => {
		setSelectedFiles([])
		setSelectedFolder(initialFolderId || null)
		setFileTags({})
		setStep(1)
		setFileError('')
		setTagSearch('')
		onClose()
	}

	const handleFolderSelect = folderId => setSelectedFolder(folderId)

	const handleTagToggle = (fileName, tagId) => {
		setFileTags(prev => {
			const prevTags = prev[fileName] || []
			const exists = prevTags.includes(tagId)
			const newTags = exists ? prevTags.filter(id => id !== tagId) : prevTags.length < 10 ? [...prevTags, tagId] : prevTags
			return { ...prev, [fileName]: newTags }
		})
		setTagSearch('')
	}

	const handleNext = () => {
		if (step === 1 && selectedFiles.length === 1) setStep(2)
		if (step === 2 && selectedFiles.every(f => (fileTags[f.name]?.length || 0) >= 5)) setStep(3)
	}
	const handleBack = () => {
		if (step === 2) {
			setFileTags({})
			setTagSearch('')
		}
		if (step === 3) {
			setSelectedFolder(initialFolderId || null)
		}
		setStep(prev => Math.max(1, prev - 1))
	}

	const handleSelectFiles = files => {
		let error = ''
		const validFiles = []
		for (const file of files) {
			if (!allowedTypes.includes(file.type)) {
				error = 'Archivo inválido. Tipos permitidos: PDF, DOCX, PPTX, XLSX, TXT o imagen.'
				continue
			}
			if (file.size > 10 * 1024 * 1024) {
				error = 'Tamaño máximo de archivo: 10 MB.'
				continue
			}
			validFiles.push(file)
		}
		setFileError(error)
		setSelectedFiles(validFiles.slice(0, 1))
	}

	const handleSubmit = () => {
		if (selectedFiles.length === 1 && selectedFolder && selectedFiles.every(f => (fileTags[f.name]?.length || 0) >= 5)) {
			const file = selectedFiles[0]
			const tags = fileTags[file.name] || []
			const data = {
				companyId: companyId,
				title: file.name,
				fileName: file.name,
				tags,
				folderId: selectedFolder,
				...(isEmployee && { employeeId: user.id }),
			}
			onSubmit(data, file)
			handleClose()
		}
	}

	if (!isOpen) return null

	return (
		<div className='tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center'>
			<div className='tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-4 md:tw-p-6 tw-h-[50vh] md:tw-h-[80vh] tw-w-[95%] md:tw-w-[90%] lg:tw-w-[816px] tw-flex tw-flex-col tw-relative'>
				<div
					onClick={handleClose}
					className='tw-absolute tw-top-4 tw-right-4 tw-text-gray-400 hover:tw-text-gray-700 tw-text-2xl tw-font-bold tw-z-10 tw-cursor-pointer'
					aria-label='Cerrar'
					title='Cerrar'
				>
					X
				</div>
				<div className='tw-text-lg tw-font-medium tw-mb-6'>Nuevo documento</div>

				{step === 1 && (
					<>
						<div className='tw-font-bold tw-mb-2 tw-text-sm md:tw-text-base'>Paso 1: Subir documento</div>
						<UploadDocuments onSelectFiles={handleSelectFiles} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} maxFiles={1} />
						{fileError && <div className='tw-text-error-600 tw-text-xs tw-mt-2'>{fileError}</div>}
						<div className='tw-text-xs tw-text-gray-500 tw-mt-2'>
							Permitido: PDF, DOCX, PPTX, XLSX, TXT o imagen. Tamaño máximo: 10 MB. <br />
							<strong>Solo 1 documento por vez.</strong>
						</div>
						<div className='tw-flex-1' />
						<div className='tw-flex tw-justify-end tw-gap-3 tw-mt-6'>
							<div onClick={handleClose} className='border tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg'>
								Cancelar
							</div>
							<div
								onClick={handleNext}
								className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg ${
									selectedFiles.length !== 1 ? 'tw-opacity-50 tw-pointer-events-none' : ''
								}`}
							>
								Avanzar
							</div>
						</div>
					</>
				)}

				{step === 2 && (
					<>
						<div className='tw-font-bold tw-mb-2 tw-text-sm md:tw-text-base'>Paso 2: Asignar tags (mínimo 5)</div>
						<div className='tw-flex-1 tw-flex tw-flex-col tw-h-full'>
							{selectedFiles.map(file => (
								<div key={file.name} className='tw-bg-white tw-shadow tw-rounded-xl tw-p-3 md:tw-p-5 tw-mb-2 border tw-h-full tw-flex tw-flex-col'>
									<div className='tw-flex tw-items-center tw-gap-3 tw-mb-6'>
										{file.type.startsWith('image') ? (
											<IoImageOutline className='tw-text-2xl tw-text-purple-400' />
										) : (
											<IoDocumentTextOutline className='tw-text-2xl tw-text-purple-400' />
										)}
										<span className='tw-text-gray-800 tw-text-base tw-font-semibold'>{file.name}</span>
									</div>
									<div className='border-bottom tw-mb-4'></div>

									<div className='tw-mb-4'>
										<input
											className='border tw-rounded-lg tw-px-3 tw-py-2 tw-w-full tw-text-sm'
											value={tagSearch}
											onChange={e => setTagSearch(e.target.value)}
											placeholder='Buscar tags...'
										/>
									</div>

									<div className='tw-flex tw-flex-wrap tw-gap-2 tw-mb-1 tw-overflow-y-auto tw-max-h-[180px] 2xl:tw-max-h-[720px] tw-min-h-[40px]'>
										{tagList &&
											tagList
												.filter(tag => tag.name.toLowerCase().includes(tagSearch.toLowerCase()))
												.map(tag => {
													const selected = (fileTags[file.name] || []).includes(tag.id)
													return (
														<span
															key={tag.id}
															className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-cursor-pointer ${
																selected ? 'tw-bg-purple-500 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-purple-100'
															}`}
															onClick={() => handleTagToggle(file.name, tag.id)}
														>
															{tag.name}
														</span>
													)
												})}
									</div>
								</div>
							))}
						</div>
						<div className='tw-flex tw-justify-end tw-gap-3 tw-mt-6'>
							<div onClick={handleBack} className='border tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg'>
								Volver
							</div>
							<div
								onClick={handleNext}
								className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg ${
									!selectedFiles.every(f => (fileTags[f.name]?.length || 0) >= 5) ? 'tw-opacity-50 tw-pointer-events-none' : ''
								}`}
							>
								Avanzar
							</div>
						</div>
					</>
				)}

				{step === 3 && (
					<>
						<div className='tw-font-bold tw-mb-2 tw-text-sm md:tw-text-base'>Paso 3: Seleccionar carpeta</div>
						<SelectFolder folders={folders} onSelect={handleFolderSelect} selectedFolder={selectedFolder} />
						<div className='tw-flex-1' />
						<div className='tw-flex tw-justify-end tw-gap-3 tw-mt-6'>
							<div onClick={handleBack} className='border tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg'>
								Volver
							</div>
							<div
								onClick={handleSubmit}
								className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg ${
									!selectedFolder ? 'tw-opacity-50 tw-pointer-events-none' : ''
								}`}
							>
								Subir documento
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	)
}
